<template>
    <picture v-if="!isBackgroundImage" :data-iesrc="fallbackUrl || imageUrl" :data-picture-class="pictureClass" :data-alt="alt" :data-title="title">
        <slot name="additionalimages"></slot>
        <source v-if="avifUrl" :srcset="avifUrl" type="image/avif">
        <source :srcset="imageUrl" :type="mimeTypeOf(imageUrl)">
        <source v-if="fallbackUrl" :srcset="fallbackUrl" :type="mimeTypeOf(fallbackUrl)">
        <img :class="pictureClass" :src="fallbackUrl || imageUrl" :alt="alt" :title="title" :loading="loading" :fetchpriority="fetchpriority">
    </picture>
    <div v-else :data-background-image="backgroundSource" :class="pictureClass">
        <slot></slot>
    </div>
</template>

<script>
import lozad from "../../plugins/lozad";
import { detectWebP } from "../../helper/featureDetect";
import { avifUrl as buildAvifUrl } from "../../helper/avif";

export default {
    props: {
        imageUrl: String,
        fallbackUrl: String,
        isBackgroundImage: Boolean,
        pictureClass: String,
        alt: String,
        title: String,

        // Above the fold pass loading="eager" and fetchpriority="high". A lazily
        // loaded LCP image is deferred until layout and costs exactly the metric
        // it is meant to improve.
        loading: {
            type: String,
            default: "lazy"
        },
        fetchpriority: String
    },

    data()
    {
        return {
            supported: undefined
        }
    },

    mounted()
    {
        detectWebP(((supported) =>
        {
            this.supported = supported;
            this.$nextTick(() =>
            {
                if(!this.isBackgroundImage)
                {
                    this.$el.classList.toggle("lozad");
                }
                lozad(this.$el).observe();
            });
        }));
    },

    watch:
    {
        imageUrl()
        {
            this.$nextTick(() =>
            {
                this.$el.setAttribute("data-loaded", false);
                lozad(this.$el).triggerLoad(this.$el);
            });
        }
    },

    methods: {
        /**
         * Derive the mime type from the file extension, the way current Ceres
         * does it. A <source> without type matches everything, so this only
         * sharpens the negotiation - it never changes which file a browser is
         * able to pick.
         */
        mimeTypeOf(url)
        {
            const matches = url?.match(/\.(\w+)(?:$|\?)/);

            if (!matches)
            {
                return null;
            }

            const types = {
                jpg: "image/jpeg",
                jpeg: "image/jpeg",
                png: "image/png",
                gif: "image/gif",
                webp: "image/webp",
                avif: "image/avif",
                svg: "image/svg+xml"
            };

            return types[matches[1].toLowerCase()] || null;
        }
    },

    computed: {
        /**
         * The AVIF variant of this image, or null when it must not be offered.
         * The path and config checks behind it live in helper/avif, because
         * ItemImageCarousel needs exactly the same rules.
         */
        avifUrl() {
            return buildAvifUrl(this.fallbackUrl || this.imageUrl);
        },

        /**
         *  Determine appropriate image url to use as background source
         */
        backgroundSource() {
            if(this.imageUrl && this.isWebpImage){
                return this.supported ? this.imageUrl : this.fallbackUrl;
            } else {
                return this.imageUrl || this.fallbackUrl;
            }
        },

        /**
         * True only when imageUrl points to a .webp file.
         *
         * backgroundSource() needs exactly this yes/no answer to decide whether
         * the fallback is required. It must NOT be replaced by mimeTypeOf(),
         * which returns a type for every format - that would send browsers
         * without webp support to the fallback even for plain JPEGs.
         */
        isWebpImage() {
            const matches = this.imageUrl?.match(/.?(\.\w+)(?:$|\?)/);

            if(matches)
            {
                return matches[1].toLowerCase() === ".webp";
            }

            return false;
        }
    }
}
</script>
